import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/shared/status-badge";
import {
  getGetApiAiProviderConnectionsQueryKey,
  postApiAiProviderConnectionsCredentialGet,
  useDeleteApiAiProviderConnectionsId,
  usePostApiAiProviderConnections,
  usePostApiAiProviderConnectionsCredentialSave,
  usePostApiAiProviderConnectionsValidate,
} from "@/lib/api/generated/providers/providers";
import type {
  TenantAiProviderConnectionRecordDto,
} from "@/lib/api/generated/control-api.schemas";
import { queryKeys } from "@/lib/query/keys";
import { toastMutationError } from "@/lib/utils/formatting";
import type { AiProviderMetadata } from "./provider-metadata.js";
import {
  createProviderDraft,
  getProviderOnboardingConfig,
  toConnectionRecord,
  type ProviderDraft,
} from "./provider-onboarding.js";

export function ProviderConnectionCard(params: {
  tenantId: string;
  metadata: AiProviderMetadata;
  existing: TenantAiProviderConnectionRecordDto | null;
  modelCount: number;
}) {
  const { tenantId, metadata, existing, modelCount } = params;
  const queryClient = useQueryClient();
  const onboarding = getProviderOnboardingConfig(tenantId, metadata);
  const [draft, setDraft] = useState<ProviderDraft>(() =>
    createProviderDraft(tenantId, metadata, existing),
  );
  const [showAdvanced, setShowAdvanced] = useState(Boolean(onboarding.showAdvancedByDefault));
  const profileId = draft.profileId.trim();
  const credentialQuery = useQuery({
    queryKey: ["tenantAiProviderCredential", tenantId, profileId],
    queryFn: () => postApiAiProviderConnectionsCredentialGet({ profileId }),
    enabled: Boolean(tenantId && profileId),
  });
  const saveMutation = usePostApiAiProviderConnections({
    mutation: {
      onSuccess: (savedConnection) => {
        writeConnectionCache(savedConnection);
        invalidateProviderQueries();
      },
    },
  });
  const saveCredentialMutation = usePostApiAiProviderConnectionsCredentialSave({
    mutation: {
      onSuccess: (_data, input) => {
        queryClient.invalidateQueries({ queryKey: ["tenantAiProviderCredential", tenantId, input.data.profileId] });
      },
    },
  });
  const validateMutation = usePostApiAiProviderConnectionsValidate({
    mutation: {
      onSuccess: (result) => {
        writeConnectionCache(result.connection);
        invalidateProviderQueries();
      },
    },
  });
  const deleteMutation = useDeleteApiAiProviderConnectionsId({
    mutation: {
      onSuccess: () => {
        queryClient.setQueryData<TenantAiProviderConnectionRecordDto[]>(
          getGetApiAiProviderConnectionsQueryKey(),
          (current) => (current ?? []).filter((connection) => connection.id !== existing?.id),
        );
        invalidateProviderQueries();
      },
    },
  });
  const status = existing?.status ?? "disconnected";
  const hasStoredCredential =
    credentialQuery.data?.hasKey ?? credentialQuery.data?.hasCredential ?? false;
  const isBusy =
    saveMutation.isPending ||
    saveCredentialMutation.isPending ||
    validateMutation.isPending ||
    deleteMutation.isPending;

  const connectionRecord = toConnectionRecord(
    tenantId,
    metadata,
    draft,
    existing,
  );

  function invalidateProviderQueries() {
    queryClient.invalidateQueries({ queryKey: getGetApiAiProviderConnectionsQueryKey() });
    queryClient.invalidateQueries({ queryKey: queryKeys.tenantAiProviderConnections(tenantId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.tenantModelCatalog(tenantId) });
    queryClient.invalidateQueries({ queryKey: queryKeys.state });
  }

  function writeConnectionCache(connection: TenantAiProviderConnectionRecordDto) {
    queryClient.setQueryData<TenantAiProviderConnectionRecordDto[]>(
      getGetApiAiProviderConnectionsQueryKey(),
      (current) => {
        const previous = current ?? [];
        const existingIndex = previous.findIndex((entry) => entry.id === connection.id);
        if (existingIndex < 0) {
          return [...previous, connection];
        }
        return previous.map((entry, index) => (index === existingIndex ? connection : entry));
      },
    );
  }

  async function saveCredentialIfNeeded() {
    if (metadata.authMode !== "api_key") {
      return;
    }
    const apiKey = draft.apiKey.trim();
    if (!apiKey) {
      if (existing && hasStoredCredential) {
        return;
      }
      throw new Error("API key is required.");
    }
    await saveCredentialMutation.mutateAsync({
      data: {
        providerId: metadata.providerId,
        profileId,
        apiKey,
        capabilityType: "ai_model",
      },
    });
  }

  async function handleSave() {
    try {
      await saveCredentialIfNeeded();
      await saveMutation.mutateAsync({
        data: connectionRecord,
      });
      toast.success(`${metadata.label} saved`);
      if (metadata.authMode === "api_key") {
        setDraft((current) => ({ ...current, apiKey: "" }));
      }
    } catch (err) {
      toast.error(toastMutationError(err, `Failed to save ${metadata.label}`));
    }
  }

  async function handleValidate() {
    try {
      await saveCredentialIfNeeded();
      await saveMutation.mutateAsync({
        data: connectionRecord,
      });
      const result = await validateMutation.mutateAsync({ data: { id: connectionRecord.id } });
      if (result.issues.length === 0) {
        toast.success(`${metadata.label} validated`);
        if (metadata.authMode === "api_key") {
          setDraft((current) => ({ ...current, apiKey: "" }));
        }
        return;
      }
      toast.error(result.issues.map((issue) => issue.message).join(" · "));
    } catch (err) {
      toast.error(toastMutationError(err, `Failed to validate ${metadata.label}`));
    }
  }

  async function handlePrimaryAction() {
    if (onboarding.primaryActionKind === "save_and_validate") {
      await handleValidate();
      return;
    }
    await handleSave();
  }

  async function handleDelete() {
    if (!existing) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({
        id: existing.id,
      });
      toast.success(`${metadata.label} removed`);
      setDraft(createProviderDraft(tenantId, metadata, null));
    } catch (err) {
      toast.error(toastMutationError(err, `Failed to delete ${metadata.label}`));
    }
  }

  return (
    <Card className="h-full">
      <CardHeader className="gap-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <CardTitle className="text-base">{metadata.label}</CardTitle>
            <CardDescription>{metadata.description}</CardDescription>
          </div>
          <StatusBadge status={status} />
        </div>
        <div
          className="flex flex-wrap gap-2 text-[11px]"
          style={{ color: "var(--muted-foreground)" }}
        >
          <Badge variant="outline">
            {metadata.authMode === "api_key" ? "API key" : "OAuth"}
          </Badge>
          <Badge variant="outline">
            {modelCount} catalog model{modelCount === 1 ? "" : "s"}
          </Badge>
          {existing?.lastValidatedAt ? (
            <Badge variant="outline">Validated {formatShortDate(existing.lastValidatedAt)}</Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div
          className="rounded-[var(--radius-sm)] border px-3 py-3"
          style={{
            borderColor: "var(--border)",
            background: "color-mix(in srgb, var(--surface) 88%, var(--bg-accent))",
          }}
        >
          <div className="text-sm font-medium" style={{ color: "var(--text-strong)" }}>
            Recommended onboarding
          </div>
          <p className="mt-1 text-sm" style={{ color: "var(--muted-foreground)" }}>
            {onboarding.summary}
          </p>
          <ol className="mt-3 list-decimal space-y-1 pl-4 text-xs" style={{ color: "var(--muted-foreground)" }}>
            {onboarding.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>

        {onboarding.showLabelInput ? (
          <Field label="Connection label">
            <input
              value={draft.label}
              onChange={(event) =>
                setDraft((current) => ({ ...current, label: event.target.value }))
              }
              className="form-input"
            />
          </Field>
        ) : null}

        {onboarding.showPrimaryCredentialInput ? (
          <Field
            label={onboarding.primaryCredentialLabel ?? "API key"}
            hint={
              hasStoredCredential && !draft.apiKey.trim()
                ? "A credential is already stored. Enter a new API key only when rotating it."
                : onboarding.primaryCredentialHint
            }
          >
            <input
              type="password"
              value={draft.apiKey}
              onChange={(event) =>
                setDraft((current) => ({ ...current, apiKey: event.target.value }))
              }
              placeholder={hasStoredCredential ? "Stored in Postgres" : ""}
              className="form-input font-mono"
            />
          </Field>
        ) : (
          <ReadOnlyField label={onboarding.primaryReadOnlyLabel ?? "Runtime profile"}>
            {onboarding.primaryReadOnlyValue ?? draft.profileId}
          </ReadOnlyField>
        )}

        {onboarding.showModelListInput ? (
          <Field
            label="Models"
            hint="Comma-separated model ids to expose in tenant model selection."
          >
            <textarea
              value={draft.modelIdsText}
              onChange={(event) =>
                setDraft((current) => ({ ...current, modelIdsText: event.target.value }))
              }
              className="form-input min-h-24"
              placeholder="gpt-5.4, gpt-5.4-mini"
            />
          </Field>
        ) : null}

        {existing?.lastError ? (
          <div
            className="rounded-[var(--radius-sm)] border px-3 py-2 text-xs"
            style={{
              background: "color-mix(in srgb, var(--danger) 10%, transparent)",
              borderColor: "color-mix(in srgb, var(--danger) 40%, var(--border))",
              color: "var(--text-strong)",
            }}
          >
            {existing.lastError}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => void handlePrimaryAction()} disabled={isBusy}>
            {onboarding.primaryActionLabel}
          </Button>
          <Button size="sm" variant="outline" onClick={() => void handleValidate()} disabled={isBusy}>
            Validate
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowAdvanced((current) => !current)}
            disabled={isBusy}
          >
            {showAdvanced ? "Hide advanced" : "Advanced"}
          </Button>
          {existing ? (
            <Button size="sm" variant="destructive" onClick={() => void handleDelete()} disabled={isBusy}>
              Delete
            </Button>
          ) : null}
        </div>

        {showAdvanced ? (
          <div className="grid gap-4 rounded-[var(--radius-sm)] border p-3" style={{ borderColor: "var(--border)" }}>
            <Field label="Connection label">
              <input
                value={draft.label}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, label: event.target.value }))
                }
                className="form-input"
              />
            </Field>
            <ReadOnlyField label="Connection ID">{draft.id}</ReadOnlyField>
            <ReadOnlyField label="Profile ID">{draft.profileId}</ReadOnlyField>
            <ReadOnlyField label="Credential storage">
              {hasStoredCredential ? "Stored in Postgres" : "No credential stored"}
            </ReadOnlyField>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field(params: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {params.label}
      </label>
      {params.children}
      {params.hint ? (
        <span className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
          {params.hint}
        </span>
      ) : null}
    </div>
  );
}

function ReadOnlyField(params: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {params.label}
      </span>
      <div
        className="rounded-[var(--radius-sm)] border px-3 py-2 font-mono text-sm"
        style={{ borderColor: "var(--border)", color: "var(--muted-foreground)" }}
      >
        {params.children}
      </div>
    </div>
  );
}

function formatShortDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}
